import VariableChip from '@/components/common/chips/VariableChip';
import VariablePicker from '@/components/nodes/settings/variable/VariablePicker';
import { useMetadata } from '@/hooks/useMetadata';
import { Application } from '@/types/application';
import { PromptGroup } from '@/types/prompt';
import { VariableRef } from '@/types/variable';
import { isString, isVariable } from '@/util/typeGuards';
import { getScopeType, getVariableTypeLabel } from '@/util/variables';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { FC, useEffect, useState } from 'react';

type EditPromptGroupsDialogProps = {
  open: boolean;
  promptGroup?: PromptGroup;
  application: Application;
  onClose: () => void;
  setApplication: (newApplication: Application) => void;
};

const EditPromptGroupsDialog: FC<EditPromptGroupsDialogProps> = (props) => {
  const { metadata } = useMetadata();
  const [name, setName] = useState<string>();
  const [path, setPath] = useState<VariableRef | string>('');
  const [openVars, setOpenVars] = useState(false);

  const handleConfirm = () => {
    if (!name) return;
    editPromptGroup(name, path);
    setName('');
    setPath('');
    props.onClose();
  };

  const editPromptGroup = (newName: string, newPath: VariableRef | string) => {
    props.setApplication({
      ...props.application,
      promptGroups: props.application.promptGroups?.map((group) => {
        if (group.id === props.promptGroup?.id) {
          return { name: newName, path: newPath, id: group.id };
        } else {
          return group;
        }
      }),
      prompts: props.application.prompts.map((prompt) => {
        if (prompt.promptGroup === props.promptGroup?.id) {
          return { ...prompt, path: newPath };
        } else {
          return prompt;
        }
      })
    });
  };

  useEffect(() => {
    if (!props.promptGroup) return;
    setName(props.promptGroup?.name);
    setPath(props.promptGroup?.path || '');
  }, [props.open]);

  const variableType = isVariable(path) ? getVariableTypeLabel(path, props.application, metadata) : undefined;

  return (
    <Dialog open={props.open} onClose={props.onClose} disableRestoreFocus>
      <DialogTitle>{'Edit Prompt Group'}</DialogTitle>
      <DialogContent>
        <TextField
          label={'name'}
          size='small'
          fullWidth
          autoFocus={props.open}
          value={name || ''}
          onChange={(event) => setName(event.target.value)}
          sx={{ mt: 1 }}
          InputProps={{ autoComplete: 'off' }}
        />
        <Box display={'flex'} mt={2} gap={2}>
          <TextField
            label={'path'}
            size='small'
            value={isString(path) ? path : ''}
            onChange={(event) => setPath(event.target.value)}
            sx={{ mt: 1 }}
            InputProps={{ autoComplete: 'off' }}
          />
          <VariableChip
            onClick={() => setOpenVars(true)}
            onDelete={() => setPath('')}
            type={undefined}
            variableType={variableType}
            value={!isString(path) ? path : undefined}
            chipText={!isString(path) ? path?.name : undefined}
            scope={!isString(path) ? path?.scope : undefined}
          />
          <VariablePicker
            subroutineId={1}
            open={openVars}
            onClose={() => setOpenVars(false)}
            type={['string']}
            scope={getScopeType('promptgroups', metadata)}
            onVariablePicked={(variable) => setPath(variable)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color='primary'>
          {'Cancel'}
        </Button>
        <Button onClick={handleConfirm} disabled={!name} color='primary'>
          {'Ok'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPromptGroupsDialog;
